// views/Chat/helper.js

import { inject } from "vue";
import { useStore } from "vuex";

export function useMainChat() {
    const companyId = inject("$companyId");
    const userId = inject("$userId");
    const { getters, dispatch } = useStore();

    /**
     * Get chat projects
     */
    function getProjects() {
        return new Promise((resolve, reject) => {
            try {
                if (getters["mainChat/mainChatProjects"]?.data?.length) {
                    resolve(true);
                } else {
                    dispatch("mainChat/setChatProjects")
                        .then(() => {
                            resolve(true);
                        })
                        .catch((error) => {
                            console.error("ERROR in set project chat:", error);
                            reject(error);
                        });
                }
            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * ✅ PRIMARY METHOD: Dispatch unified conversations (group chat + 1:1)
     * 
     * Flow mới: Cả groupChat và oneToOne đều lấy từ API /conversations
     * Backend trả về tất cả conversations của user với chatType để phân biệt
     * 
     * @param {Object} options
     * @param {string} options.companyId - Company ID
     * @param {string} options.userId - User ID
     * @param {string} options.chatType - Optional: 'groupChat' | 'oneToOne' | undefined (all)
     * @param {string} options.search - Optional: Search keyword
     * @param {boolean} options.skipIfLoaded - Skip API call if data already loaded
     */
    function dispatchConversations(options = {}) {
        return new Promise((resolve, reject) => {
            try {
                const payload = {
                    companyId: options.companyId || companyId.value,
                    userId: options.userId || userId.value,
                    chatType: options.chatType || undefined,
                    search: options.search || undefined,
                };
                
                // Check if already loaded
                const existingData = getters["mainChat/conversations"]?.data || [];

                if (options.skipIfLoaded && existingData.length > 0) {
                    resolve(existingData);
                    return;
                }

                dispatch("mainChat/setConversations", payload)
                    .then((conversations) => {
                        resolve(conversations);
                    })
                    .catch((error) => {
                        console.error("[useMainChat] Error loading conversations:", error);
                        reject(error);
                    });
            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * Get conversations from store (no API call)
     */
    function getConversations() {
        return getters["mainChat/conversations"]?.data || [];
    }

    /**
     * Get group chats from store (filtered from conversations)
     */
    function getGroupChats() {
        return getters["mainChat/groupChatConversations"] || [];
    }

    /**
     * Get 1:1 chats from store (filtered from conversations)
     */
    function getOneToOneChats() {
        return getters["mainChat/oneToOneConversations"] || [];
    }

    /**
     * Check if conversations are loading
     */
    function isLoading() {
        return getters["mainChat/isConversationsLoading"] || false;
    }

    // ============================================================
    // LEGACY METHODS - DEPRECATED
    // Giữ lại để backward compatibility nhưng không nên sử dụng
    // Flow mới: Sử dụng dispatchConversations() để lấy cả groupChat và oneToOne
    // ============================================================

    /**
     * @deprecated Use dispatchConversations instead
     * Legacy: Dispatch 1:1 chats only
     * 
     * LƯU Ý: Method này đã deprecated. Flow mới lấy tất cả conversations
     * từ một API duy nhất. Chỉ giữ lại để backward compatibility.
     */
    function dispatchChats(projectId = null) {
        console.warn("[useMainChat] dispatchChats is DEPRECATED. Use dispatchConversations instead.");
        
        return new Promise((resolve, reject) => {
            try {
                if (!projectId) {
                    throw new Error("NO project");
                }

                // Redirect to new method
                dispatchConversations({
                    companyId: companyId.value,
                    userId: userId.value,
                })
                    .then(resolve)
                    .catch(reject);

                // OLD CODE - COMMENTED OUT
                // if (getters["mainChat/chats"]?.data?.length) {
                //     resolve(getters["mainChat/chats"]?.data?.length);
                // } else {
                //     dispatch("mainChat/setChats", {
                //         projectId: projectId,
                //         companyId: companyId.value,
                //         userId: userId.value,
                //         sprintId
                //     })
                //         .then((chats) => {
                //             resolve(chats);
                //         })
                //         .catch((error) => {
                //             reject(error);
                //         });
                // }
            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * @deprecated Use dispatchConversations instead
     * Legacy: Dispatch group chats only
     * 
     * LƯU Ý: Method này đã deprecated. Flow mới lấy tất cả conversations
     * từ một API duy nhất với chatType filter.
     */
    function dispatchGroupChats(options = {}) {
        console.warn("[useMainChat] dispatchGroupChats is DEPRECATED. Use dispatchConversations instead.");
        
        return new Promise((resolve, reject) => {
            try {
                // Redirect to new method with chatType filter
                dispatchConversations({
                    companyId: options.companyId || companyId.value,
                    userId: options.userId || userId.value,
                    chatType: 'groupChat', // Filter only group chats
                    search: options.search || undefined,
                })
                    .then(resolve)
                    .catch(reject);

                // OLD CODE - COMMENTED OUT
                // const payload = {
                //     companyId: options.companyId || companyId.value,
                //     userId: options.userId || userId.value,
                //     search: options.search || undefined,
                // };
                //
                // dispatch("mainChat/setGroupChats", payload)
                //     .then((groupChats) => {
                //         resolve(groupChats);
                //     })
                //     .catch((error) => {
                //         reject(error);
                //     });
            } catch (error) {
                reject(error);
            }
        });
    }

    return {
        // ✅ PRIMARY METHODS (Use these)
        dispatchConversations,
        getConversations,
        getGroupChats,
        getOneToOneChats,
        isLoading,
        getProjects,
        
        // ⚠️ DEPRECATED (Backward compatibility only)
        dispatchChats,
        dispatchGroupChats,
    };
}